import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";
import { getPageBackground, getCardBackground, getPrimaryText, getSecondaryText, getMutedText } from "../utils/themeUtils";
import config from "../api/config";
import { getHeaders } from "../api/apiHelpers";
import { useDispatch } from "react-redux";
import { establishSession, SIGN_IN_NOT_SAVED_MESSAGE } from "../utils/establishSession";
import { reportSignInFailed } from "../api/sessionDiagnostics";

const MagicLogin: React.FC = () => {
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch();
  // A link works once. This keeps the page from sending the same link twice
  // (React runs effects twice in development; the second send would be
  // refused and show "invalid" over a sign-in that worked).
  const sentToken = useRef<string | null>(null);

  // Extract tenant ID from URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  // Extract token from URL query params
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyAndLogin = async () => {
      if (!token) {
        setVerifying(false);
        setError("No magic link token provided. Please request a new login link.");
        return;
      }

      if (sentToken.current === token) return;
      sentToken.current = token;

      try {
        setLoggingIn(true);
        const response = await fetch(`${config.BASE_URL}/auth/verify-magic-link`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        // The server answers a good link exactly as it answers a password
        // sign-in: { token, user, source }. Anything else is not a sign-in.
        //
        // 2026-10-08: this page looked for `success` and `session.access_token`
        // (never sent) and wrote `access_token` / `refresh_token` (never read),
        // so a valid link used itself up and then said "invalid".
        if (response.ok && data?.valid === true && typeof data.token === "string" && data.token) {
          // The SAME function the sign-in page uses. No storage is written here.
          const session = await establishSession(data, { dispatch, tenantId });
          if (!session.ok) {
            setError(SIGN_IN_NOT_SAVED_MESSAGE);
            return;
          }
          navigate(session.destination || `/${tenantId}/dashboard`, { replace: true });
        } else {
          setError(data?.error || "Invalid or expired magic link. Please request a new one.");
        }
      } catch (err) {
        reportSignInFailed(err);
        setError("Unable to verify magic link. Please try again or request a new one.");
      } finally {
        setVerifying(false);
        setLoggingIn(false);
      }
    };

    verifyAndLogin();
    // Runs once per link: a link can be used once, and `dispatch`/`navigate`
    // changing identity must not send it a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenantId]);

  // Loading state while verifying
  if (verifying || loggingIn) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar transparent={true} />
        <div className={`${getPageBackground(isDarkMode)} flex-1 flex items-center justify-center py-8 sm:py-16 px-4 sm:px-8`}>
          <div className={`${getCardBackground(isDarkMode)} py-6 px-4 sm:p-8 rounded-lg shadow-lg w-full max-w-sm sm:max-w-md text-center`}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 dark:border-gray-500 mx-auto mb-4"></div>
            <p className={`${getSecondaryText(isDarkMode)}`}>
              {loggingIn ? "Logging you in..." : "Verifying magic link..."}
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state - invalid/expired token
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar transparent={true} />
      <div className={`${getPageBackground(isDarkMode)} flex-1 flex items-center justify-center py-8 sm:py-16 px-4 sm:px-8`}>
        <div className={`${getCardBackground(isDarkMode)} py-6 px-4 sm:p-8 rounded-lg shadow-lg w-full max-w-sm sm:max-w-md`}>
          <h2 className={`text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 ${getPrimaryText(isDarkMode)}`}>
            Magic Link Invalid
          </h2>
          <div className="bg-red-50 border-l-4 border-red-400 p-3 sm:p-4 mb-6">
            <p className="text-red-700 text-sm sm:text-base">{error}</p>
          </div>
          <p className={`text-center mb-6 text-sm ${getMutedText(isDarkMode)}`}>
            Magic links expire after 1 hour and can only be used once.
            Please contact your club administrator to request a new login link.
          </p>
          <Link
            to={`/${tenantId}/login`}
            className="block w-full text-center bg-[var(--cc-action)] text-[var(--cc-action-text)] py-2 px-4 sm:py-3 sm:px-6 rounded-lg hover:bg-[var(--cc-action-hover)] transition-colors duration-200 text-sm sm:text-base font-medium"
          >
            Go to Login
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MagicLogin;
